// ============================================================================
// PHYSICS SYSTEM (Matter.js wrapper)
// ============================================================================

import Matter from 'matter-js';
import CONFIG from '../config.js';

const { Engine, World, Bodies, Body, Events, Query, Constraint } = Matter;

export class PhysicsSystem {
    // Collision categories (bitmasks)
    static CATEGORY = {
        DEFAULT: 0x0001,
        PLAYER: 0x0002,
        WALL: 0x0004,
        FURNITURE: 0x0008,
        THROWABLE: 0x0010,  // Detached doors, mirror pieces, etc.
        DOOR: 0x0020
    };
    
    // Collision masks (what each category collides with)
    static MASK = {
        // Player collides with walls, furniture, doors (NOT throwables)
        PLAYER: 0x0001 | 0x0004 | 0x0008 | 0x0020,
        // Throwables collide with walls, furniture, doors (NOT player)
        THROWABLE: 0x0001 | 0x0004 | 0x0008 | 0x0020,
        // Everything else collides with everything
        ALL: 0xFFFF
    };
    
    constructor(worldWidth, worldHeight) {
        // Create Matter.js engine
        this.engine = Engine.create();
        this.world = this.engine.world;
        
        // Configure engine
        this.world.gravity.y = 0; // No gravity for top-down game
        this.world.gravity.x = 0;
        
        // Set solver iterations for collision resolution (from config)
        this.engine.positionIterations = CONFIG.physicsPositionIterations;
        this.engine.velocityIterations = CONFIG.physicsVelocityIterations;
        
        // Store world bounds
        this.worldWidth = worldWidth;
        this.worldHeight = worldHeight;
        
        // Create debug renderer (will be initialized when canvas is available)
        this.debugRenderer = null;
        this.debugCanvas = null;
        
        // Map to store entity -> body relationships
        this.entityBodies = new Map();
        
        // Map to store carry constraints
        this.carryConstraints = new Map();
        
        // WeakMap to store original inertia values for carried objects (so we can restore them)
        this.originalInertias = new WeakMap();
        
        // Collision callback (will be set by game)
        this.onThrowableCollision = null;
    }
    
    /**
     * Set callback for throwable collision events
     * @param {Function} callback - (throwableBody, velocity) => void
     */
    setThrowableCollisionCallback(callback) {
        this.onThrowableCollision = callback;
        
        // Listen for collision events
        Events.on(this.engine, 'collisionStart', (event) => {
            const pairs = event.pairs;
            
            for (const pair of pairs) {
                const bodyA = pair.bodyA;
                const bodyB = pair.bodyB;
                
                // Check if either body is a throwable
                const throwableBody = (bodyA.collisionFilter.category === PhysicsSystem.CATEGORY.THROWABLE) ? bodyA : 
                                     (bodyB.collisionFilter.category === PhysicsSystem.CATEGORY.THROWABLE) ? bodyB : null;
                
                if (throwableBody && this.onThrowableCollision) {
                    // Calculate impact velocity
                    const velocity = Math.sqrt(
                        throwableBody.velocity.x * throwableBody.velocity.x + 
                        throwableBody.velocity.y * throwableBody.velocity.y
                    );
                    
                    // Only trigger if velocity is significant (avoid tiny movements)
                    if (velocity > 10) {
                        this.onThrowableCollision(throwableBody, velocity);
                    }
                }
            }
        });
    }

    // Initialize debug renderer (no longer needed - we draw directly to main canvas)
    initDebugRenderer(canvas, ctx) {
        // No-op: debug rendering now draws directly to the main canvas
    }
    
    // Create static wall bodies from tilemap
    createWallBodies(tilemap) {
        const tileSize = tilemap.tileSize;
        const wallBodies = [];
        
        // Track which tiles have been processed (for merging adjacent walls)
        const processed = [];
        for (let row = 0; row < tilemap.rows; row++) {
            processed[row] = new Array(tilemap.cols).fill(false);
        }
        
        // Create optimized wall bodies by merging horizontally adjacent walls
        for (let row = 0; row < tilemap.rows; row++) {
            let startCol = null;
            
            for (let col = 0; col <= tilemap.cols; col++) {
                const tile = tilemap.tiles[row]?.[col];
                const isWall = tile && !tilemap.tileTypes[tile.type].walkable;
                
                if (isWall && startCol === null) {
                    // Start of a wall segment
                    startCol = col;
                } else if (!isWall && startCol !== null) {
                    // End of a wall segment - create body
                    const width = (col - startCol) * tileSize;
                    const height = tileSize;
                    const centerX = startCol * tileSize + width / 2;
                    const centerY = row * tileSize + height / 2;
                    
                    const body = this.createRectangle(centerX, centerY, width, height, {
                        isStatic: true,
                        friction: 0,
                        restitution: 0,
                        label: 'wall'
                    });
                    wallBodies.push(body);
                    
                    startCol = null;
                }
            }
        }
        
        console.log(`Created ${wallBodies.length} wall bodies`);
        return wallBodies;
    }

    // Create a rectangle body
    createRectangle(x, y, width, height, options = {}) {
        const body = Bodies.rectangle(x, y, width, height, {
            isStatic: options.isStatic || false,
            frictionAir: options.frictionAir || 0,
            friction: options.friction || 0,
            restitution: options.restitution || 0,
            ...options
        });
        
        World.add(this.world, body);
        return body;
    }

    // Create a circle body
    createCircle(x, y, radius, options = {}) {
        const body = Bodies.circle(x, y, radius, {
            isStatic: options.isStatic || false,
            frictionAir: options.frictionAir || 0,
            friction: options.friction || 0,
            restitution: options.restitution || 0,
            ...options
        });
        
        World.add(this.world, body);
        return body;
    }
    
    // Create a sensor body (detects collisions but doesn't collide)
    createSensor(x, y, radius, options = {}) {
        const body = Bodies.circle(x, y, radius, {
            isStatic: true,
            isSensor: true, // Sensor: detects collisions but doesn't respond
            ...options
        });
        
        World.add(this.world, body);
        return body;
    }
    
    // Check if player is colliding with a sensor body
    isPlayerInSensor(sensorBody, playerBody) {
        if (!sensorBody || !playerBody) return false;
        // Use Matter.js collision detection
        const collisions = Query.collides(sensorBody, [playerBody]);
        return collisions && collisions.length > 0;
    }

    // Link an entity to a body
    linkEntity(entity, body) {
        this.entityBodies.set(entity, body);
        body.entity = entity; // Store reference to entity on body
    }

    // Get body for an entity
    getBody(entity) {
        return this.entityBodies.get(entity);
    }
    
    // Query for bodies at a point
    queryPoint(x, y) {
        return Query.point(this.world.bodies, { x, y });
    }
    
    // Query for bodies in a region (AABB)
    queryRegion(minX, minY, maxX, maxY) {
        const bounds = {
            min: { x: minX, y: minY },
            max: { x: maxX, y: maxY }
        };
        return Query.region(this.world.bodies, bounds);
    }

    // Remove a body
    removeBody(body) {
        World.remove(this.world, body);
        // Remove from entity map
        for (let [entity, b] of this.entityBodies.entries()) {
            if (b === body) {
                this.entityBodies.delete(entity);
                break;
            }
        }
    }

    // Update physics (call each frame)
    update(dt) {
        // Calculate sub-steps for fast moving objects
        // We want to ensure no object moves more than ~half its size per step
        // to prevent tunneling through walls.
        let maxVelocitySq = 0;
        for (let body of this.world.bodies) {
            if (body.isStatic) continue;
            const vSq = body.velocity.x * body.velocity.x + body.velocity.y * body.velocity.y;
            if (vSq > maxVelocitySq) maxVelocitySq = vSq;
        }

        const maxVelocity = Math.sqrt(maxVelocitySq);
        const maxStepDistance = CONFIG.physicsSubstepMaxDistance; // safe distance in pixels per substep
        
        // At 60fps, dt is ~0.016s. distance = velocity * dt.
        // We want (velocity * dt / subSteps) <= maxStepDistance
        // subSteps >= (velocity * dt) / maxStepDistance
        const neededSubSteps = Math.ceil((maxVelocity * dt) / maxStepDistance);
        const subSteps = Math.max(1, Math.min(neededSubSteps, CONFIG.physicsSubstepMaxCount)); // Cap at max for performance

        if (subSteps > 1) {
            const subDt = (dt * 1000) / subSteps;
            for (let i = 0; i < subSteps; i++) {
                Engine.update(this.engine, subDt);
            }
        } else {
            Engine.update(this.engine, dt * 1000); // Matter.js expects milliseconds
        }
    }

    // Render debug overlay directly to main canvas
    renderDebug(ctx, camera) {
        // Apply camera transform (same as other game objects)
        camera.apply(ctx);
        
        // Draw all bodies
        for (let body of this.world.bodies) {
            this.drawBody(ctx, body);
        }
        
        // Restore camera transform
        camera.restore(ctx);
    }

    // Draw a single body for debug
    drawBody(ctx, body) {
        ctx.save();
        
        // Set color based on body type
        if (body.isStatic) {
            ctx.strokeStyle = '#00ff00'; // Green for static
            ctx.fillStyle = 'rgba(0, 255, 0, 0.2)';
        } else {
            ctx.strokeStyle = '#ff0000'; // Red for dynamic
            ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
        }
        
        ctx.lineWidth = 2;
        
        // Draw based on body shape
        const vertices = body.vertices;
        
        ctx.beginPath();
        ctx.moveTo(vertices[0].x, vertices[0].y);
        for (let i = 1; i < vertices.length; i++) {
            ctx.lineTo(vertices[i].x, vertices[i].y);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        
        // Draw angle indicator
        if (body.angle !== 0) {
            ctx.strokeStyle = '#ffff00';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(body.position.x, body.position.y);
            ctx.lineTo(
                body.position.x + Math.cos(body.angle) * 20,
                body.position.y + Math.sin(body.angle) * 20
            );
            ctx.stroke();
        }
        
        // Draw center point
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(body.position.x, body.position.y, 2, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
    }
    
    // Draw constraints for debug
    drawConstraints(ctx, camera) {
        camera.apply(ctx);
        
        for (let constraint of this.world.constraints) {
            if (constraint.bodyA && constraint.bodyB) {
                const pointA = constraint.pointA;
                const pointB = constraint.pointB;
                const posA = constraint.bodyA.position;
                const posB = constraint.bodyB.position;
                
                ctx.strokeStyle = '#ff00ff'; // Magenta for constraints
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.moveTo(posA.x + pointA.x, posA.y + pointA.y);
                ctx.lineTo(posB.x + pointB.x, posB.y + pointB.y);
                ctx.stroke();
            } else if (constraint.bodyB) {
                // Constraint to world point
                const pointA = constraint.pointA;
                const pointB = constraint.pointB;
                const posB = constraint.bodyB.position;
                
                ctx.strokeStyle = '#ff00ff';
                ctx.lineWidth = 2;
                ctx.beginPath();
                ctx.arc(pointA.x, pointA.y, 5, 0, Math.PI * 2); // World anchor
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(pointA.x, pointA.y);
                ctx.lineTo(posB.x + pointB.x, posB.y + pointB.y);
                ctx.stroke();
            }
        }
        
        camera.restore(ctx);
    }

    // Set body position
    setPosition(body, x, y) {
        Body.setPosition(body, { x, y });
    }

    // Set body angle
    setAngle(body, angle) {
        Body.setAngle(body, angle);
    }

    // Set body velocity
    setVelocity(body, vx, vy) {
        Body.setVelocity(body, { x: vx, y: vy });
    }
    
    // Set body angular velocity
    setAngularVelocity(body, angularVelocity) {
        Body.setAngularVelocity(body, angularVelocity);
    }
    
    // Apply torque (rotational force) - adds to angular velocity, doesn't replace
    applyTorque(body, torque) {
        body.torque = torque;
    }
    
    // Check if two bodies are colliding
    checkCollision(bodyA, bodyB) {
        const collision = Query.collides(bodyA, [bodyB]);
        return collision.length > 0;
    }
    
    // Get all bodies colliding with a given body
    getCollidingBodies(body) {
        const collisions = [];
        for (let other of this.world.bodies) {
            if (other === body) continue;
            if (Query.collides(body, [other]).length > 0) {
                collisions.push(other);
            }
        }
        return collisions;
    }

    // Apply force to body at its center
    applyForce(body, forceX, forceY) {
        Body.applyForce(body, body.position, { x: forceX, y: forceY });
    }
    
    // Apply force to body at a specific point
    applyForceAtPoint(body, forceX, forceY, pointX, pointY) {
        Body.applyForce(body, { x: pointX, y: pointY }, { x: forceX, y: forceY });
    }
    
    // Create a hinge (revolute) constraint - attaches body to a world point
    createHinge(body, worldX, worldY, bodyOffsetX, bodyOffsetY) {
        const constraint = Constraint.create({
            pointA: { x: worldX, y: worldY }, // World anchor point
            bodyB: body,
            pointB: { x: bodyOffsetX, y: bodyOffsetY }, // Offset from body center
            length: 0, // Zero length = revolute joint
            stiffness: 1 // Rigid connection
        });
        
        World.add(this.world, constraint);
        return constraint;
    }
    
    // Remove a constraint
    removeConstraint(constraint) {
        World.remove(this.world, constraint);
    }
    
    // Attach a body to player (for carrying objects)
    attachToPlayer(playerBody, objectBody, offsetX = 0, offsetY = -30) {
        // Create a stiff constraint between player and object
        const constraint = Constraint.create({
            bodyA: playerBody,
            bodyB: objectBody,
            pointA: { x: offsetX, y: offsetY },
            pointB: { x: 0, y: 0 },
            length: 0,
            stiffness: 1,  // Rigid connection
            damping: 0.1
        });
        
        World.add(this.world, constraint);
        this.carryConstraints.set(objectBody, constraint);
        
        // Make object not collide with player while carried
        objectBody.collisionFilter.mask = PhysicsSystem.MASK.THROWABLE;
        
        // Disable rotation while carried by setting inertia to Infinity
        // Store original inertia so we can restore it later
        this.originalInertias.set(objectBody, objectBody.inertia);
        Body.setInertia(objectBody, Infinity);
        Body.setAngularVelocity(objectBody, 0);
        
        return constraint;
    }
    
    // Detach a body from player (for throwing)
    detachFromPlayer(objectBody) {
        const constraint = this.carryConstraints.get(objectBody);
        if (constraint) {
            World.remove(this.world, constraint);
            this.carryConstraints.delete(objectBody);
        }
        
        // Restore collision with everything except player
        objectBody.collisionFilter.mask = PhysicsSystem.MASK.THROWABLE;
        
        // Restore original inertia to allow rotation again
        if (this.originalInertias && this.originalInertias.has(objectBody)) {
            const originalInertia = this.originalInertias.get(objectBody);
            Body.setInertia(objectBody, originalInertia);
            this.originalInertias.delete(objectBody);
        }
    }
    
    // Apply collision filter to a body
    setCollisionFilter(body, category, mask) {
        body.collisionFilter.category = category;
        body.collisionFilter.mask = mask;
    }
}
